import type { ReactElement } from 'react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProcessDecisionSystemReason } from '@saas/shared';

import { Button } from '@/components/Button';
import { defaultNodeLabelKey } from '@/components/process-builder/stageRoutes';
import type { InstanceStep, SnapshotEdge, SnapshotNode } from '@/lib/processes/instance-api';
import { findRouteEdge, routeNameOf } from '@/lib/processes/step-tone';
import { systemReasonTranslation } from '@/lib/processes/system-reason';

export interface ProcessStepsTableInitiator {
  userId: string;
  comment: string | null;
  submittedAt: string;
}

export interface ProcessStepsTableProps {
  steps: InstanceStep[];
  initiator?: ProcessStepsTableInitiator | null;
  /** Mapa userId → wyświetlana nazwa (opcjonalna; fallback = surowy id). */
  userLabels?: Record<string, string>;
  /**
   * Zamrożony graf sprawy — z niego składamy opis wybranej drogi („<droga>: <skąd> → <dokąd>")
   * po `routeEdgeId` decyzji. Bez grafu decyzja pokazuje tylko swój rodzaj.
   */
  snapshot?: { nodes: SnapshotNode[]; edges: SnapshotEdge[] } | null;
  testIdPrefix?: string;
}

type GroupBy = 'stage' | 'date';

interface TableRow {
  rowKey: string;
  stepLabel: string | null;
  stepStatus: string;
  assignedNames: string;
  decisionType: string | null;
  /** Opis wybranej drogi („<droga>: <etap źródłowy> → <etap docelowy>"); `null` gdy decyzja bez drogi. */
  routeText: string | null;
  /**
   * Opis cofnięcia („Cofnięto: <etap, z którego cofnięto> → <etap, do którego wrócono>"); `null`,
   * gdy decyzja nie jest cofnięciem albo nie znamy kroku docelowego (wtedy sama etykieta „Cofnięto").
   */
  stepBackText: string | null;
  deciderName: string | null;
  whenIso: string | null;
  comment: string | null;
  /** Powód wpisu automatycznego — gdy jest, zastępuje komentarz tekstem z tłumaczeń. */
  systemReason: ProcessDecisionSystemReason | null;
}

const COLUMN_COUNT = 7;

/**
 * Typy decyzji człowieka z dedykowanymi kluczami i18n (`decision.<type>`) — panel wysyła
 * `approve` + drogę, sam komentarz (`comment`) albo „Cofnij do poprzedniego etapu" (`step_back`).
 */
const HUMAN_DECISIONS = new Set<string>(['approve', 'comment', 'step_back']);

/**
 * Gęsta tabela kroków + decyzji instancji procesu (F17 T17.1.7), z grupowaniem
 * po etapie (domyślnie) lub dacie. Służy zarówno za progres bieżącej instancji,
 * jak i historię. Komentarze renderowane jako czysty tekst (React escape'uje) —
 * nigdy dangerouslySetInnerHTML.
 */
export function ProcessStepsTable({
  steps,
  initiator = null,
  userLabels = {},
  snapshot = null,
  testIdPrefix = 'process-steps',
}: ProcessStepsTableProps): ReactElement {
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState<GroupBy>('stage');

  const nameOf = (userId: string | null): string => {
    if (!userId) return '';
    return userLabels[userId] ?? userId;
  };

  // Nazwa etapu w opisie drogi: własna nazwa węzła, a gdy jej nie ma — domyślna nazwa jego TYPU
  // (ta sama, którą pokazują kreator i diagram sprawy, np. „Akceptacja"); „—" tylko gdy nie
  // znamy nawet typu węzła.
  const stageLabelOf = (nodeId: string): string => {
    const node = snapshot?.nodes.find((n) => n.id === nodeId);
    const own = node?.label?.trim();
    if (own) return own;
    return node?.nodeType ? t(defaultNodeLabelKey(node.nodeType), '—') : '—';
  };

  // Opis drogi wybranej w decyzji: „<nazwa drogi>: <etap źródłowy> → <etap docelowy>".
  // Droga bez nazwy dostaje WŁASNĄ nazwę etapu docelowego (jak w `listAvailableRoutes` — bez
  // domyślnej nazwy typu); gdy i tej nie ma, zostaje samo „<skąd> → <dokąd>".
  // Brak krawędzi w snapshocie → `null`.
  const routeTextOf = (routeEdgeId: string | null): string | null => {
    const edge = findRouteEdge(snapshot, routeEdgeId);
    if (!edge || !snapshot) return null;
    const from = stageLabelOf(edge.sourceNodeId);
    const to = stageLabelOf(edge.targetNodeId);
    const route = routeNameOf(edge, snapshot.nodes);
    return route
      ? t('tenant.processes.stepsTable.routeTaken', { route, from, to })
      : t('tenant.processes.stepsTable.routeTakenUnnamed', { from, to });
  };
  // Nazwa etapu danego kroku: etykieta kroku, a gdy jej brak — jak `stageLabelOf` po węźle.
  const stepStageLabelOf = (step: InstanceStep): string =>
    step.label?.trim() || stageLabelOf(step.nodeId);

  // Opis cofnięcia „Cofnięto: <z> → <do>": „z" to etap kroku z decyzją, „do" — krok wskazany przez
  // `previousStepInstanceId` na tej samej liście kroków (kroków jest tyle, ile wizyt etapu, więc
  // cel to konkretna wizyta, nie etap po nazwie). Brak kroku docelowego → `null` (sama etykieta).
  const stepBackTextOf = (step: InstanceStep, decisionType: string): string | null => {
    if (decisionType !== 'step_back' || !step.previousStepInstanceId) return null;
    const target = steps.find((s) => s.stepInstanceId === step.previousStepInstanceId);
    if (!target) return null;
    return t('tenant.processes.stepsTable.stepBackTaken', {
      from: stepStageLabelOf(step),
      to: stepStageLabelOf(target),
    });
  };
  const dateOf = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');
  // „Kiedy" = data + godzina (UTC, jak dateOf) — przy kilku akcjach tego samego dnia widać
  // kolejność. decidedAt/submittedAt mają czas; wcześniej obcinaliśmy do samej daty.
  const dateTimeOf = (iso: string | null): string =>
    iso ? iso.slice(0, 16).replace('T', ' ') : '';

  // Wiersze: każda decyzja → wiersz; krok bez decyzji → wiersz „oczekujący".
  const rows: TableRow[] = [];
  for (const step of steps) {
    const assignedNames = step.assignedToUserIds.map(nameOf).filter(Boolean).join(', ');
    if (step.decisions.length > 0) {
      for (const decision of step.decisions) {
        rows.push({
          rowKey: decision.id,
          stepLabel: step.label,
          stepStatus: step.status,
          assignedNames,
          decisionType: decision.decisionType,
          routeText: routeTextOf(decision.routeEdgeId),
          stepBackText: stepBackTextOf(step, decision.decisionType),
          deciderName: nameOf(decision.decidedByUserId),
          whenIso: decision.decidedAt,
          comment: decision.comment,
          systemReason: decision.systemReason,
        });
      }
    } else {
      rows.push({
        rowKey: step.stepInstanceId,
        stepLabel: step.label,
        stepStatus: step.status,
        assignedNames,
        decisionType: null,
        routeText: null,
        stepBackText: null,
        deciderName: null,
        whenIso: null,
        comment: null,
        systemReason: null,
      });
    }
  }

  // Klucz grupy + grupowanie z zachowaniem kolejności pierwszego wystąpienia.
  const groupKeyOf = (row: TableRow): string => {
    if (groupBy === 'stage') {
      return row.stepLabel ?? t('tenant.processes.stepsTable.noStage', 'Bez etapu');
    }
    return row.whenIso
      ? dateOf(row.whenIso)
      : t('tenant.processes.stepsTable.inProgress', 'W toku');
  };
  const order: string[] = [];
  const byGroup = new Map<string, TableRow[]>();
  for (const row of rows) {
    const key = groupKeyOf(row);
    const list = byGroup.get(key);
    if (list) {
      list.push(row);
    } else {
      byGroup.set(key, [row]);
      order.push(key);
    }
  }

  const decisionLabel = (type: string | null): string => {
    if (!type) return '—';
    // 'cancelled' — anulowanie sprawy albo pominięcie kroku innej gałęzi; powód w kolumnie
    // Komentarz (z kodu powodu albo komentarza).
    if (type === 'cancelled') {
      return t('tenant.processes.stepsTable.decision.cancelled', 'Anulowano');
    }
    // 'automated_escalation' — eskalacja po terminie (decyzja przeszła na przełożonego albo
    // przełożonego tylko powiadomiono); szczegóły w kolumnie Komentarz (z kodu powodu).
    if (type === 'automated_escalation') {
      return t('tenant.processes.stepsTable.decision.automated_escalation', 'Eskalacja');
    }
    // Pozostałe decyzje automatyczne (automated_target_deleted, …) nie mają dedykowanych
    // kluczy i18n — zamiast renderować surowy klucz pokazujemy jedną generyczną etykietę.
    // Nowy typ automatyczny z własnym znaczeniem → dorobić klucz per typ (jak wyżej).
    if (!HUMAN_DECISIONS.has(type)) {
      return t('tenant.processes.stepsTable.decision.automated', 'Akcja automatyczna');
    }
    return t(`tenant.processes.stepsTable.decision.${type}`, type);
  };
  const statusLabel = (status: string): string =>
    t(`tenant.processes.stepsTable.status.${status}`, status);
  // Wpis automatyczny: tekst z tłumaczeń po kodzie powodu; inaczej komentarz osoby (albo starego
  // wpisu automatycznego, który niósł własne zdanie). Nazwy osób w tekście (przełożony przy
  // eskalacji) liczymy tak samo jak w kolumnie „Kto" — przez `nameOf`.
  const commentText = (row: TableRow): string => {
    if (!row.systemReason) return row.comment ?? '';
    const { key, params } = systemReasonTranslation(row.systemReason, nameOf);
    return t(key, params);
  };

  const headerCell = 'px-2 py-1 text-left font-medium text-text-secondary';
  const bodyCell = 'px-2 py-1 align-top';

  return (
    <div data-testid={`${testIdPrefix}-table`} className="text-sm">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-text-secondary">
          {t('tenant.processes.stepsTable.groupBy', 'Grupuj po')}:
        </span>
        <Button
          size="sm"
          variant={groupBy === 'stage' ? 'primary' : 'secondary'}
          data-testid={`${testIdPrefix}-groupby-stage`}
          aria-pressed={groupBy === 'stage'}
          onClick={() => setGroupBy('stage')}
        >
          {t('tenant.processes.stepsTable.byStage', 'Etap')}
        </Button>
        <Button
          size="sm"
          variant={groupBy === 'date' ? 'primary' : 'secondary'}
          data-testid={`${testIdPrefix}-groupby-date`}
          aria-pressed={groupBy === 'date'}
          onClick={() => setGroupBy('date')}
        >
          {t('tenant.processes.stepsTable.byDate', 'Data')}
        </Button>
      </div>

      {initiator ? (
        <div
          data-testid={`${testIdPrefix}-initiator`}
          className="mb-2 rounded border border-border-secondary bg-bg-secondary px-2 py-1"
        >
          <span className="font-medium">
            {t('tenant.processes.stepsTable.submission', 'Zgłoszenie')}
          </span>{' '}
          <span>{nameOf(initiator.userId)}</span>{' '}
          <span className="text-text-secondary">{dateTimeOf(initiator.submittedAt)}</span>
          {initiator.comment ? <div className="mt-1">{initiator.comment}</div> : null}
        </div>
      ) : null}

      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border-secondary">
            <th className={headerCell}>{t('tenant.processes.stepsTable.colStage', 'Etap')}</th>
            <th className={headerCell}>{t('tenant.processes.stepsTable.colStatus', 'Status')}</th>
            <th className={headerCell}>
              {t('tenant.processes.stepsTable.colApprovers', 'Akceptant')}
            </th>
            <th className={headerCell}>
              {t('tenant.processes.stepsTable.colDecision', 'Decyzja')}
            </th>
            <th className={headerCell}>{t('tenant.processes.stepsTable.colDecider', 'Kto')}</th>
            <th className={headerCell}>{t('tenant.processes.stepsTable.colWhen', 'Kiedy')}</th>
            <th className={headerCell}>
              {t('tenant.processes.stepsTable.colComment', 'Komentarz')}
            </th>
          </tr>
        </thead>
        <tbody>
          {order.length === 0 ? (
            <tr>
              <td colSpan={COLUMN_COUNT} className="px-2 py-3 text-center text-text-secondary">
                {t('tenant.processes.stepsTable.empty', 'Brak kroków')}
              </td>
            </tr>
          ) : (
            order.map((groupKey) => {
              const groupRows = byGroup.get(groupKey) ?? [];
              return (
                <Fragment key={groupKey}>
                  <tr data-testid={`${testIdPrefix}-group`} className="bg-bg-tertiary">
                    <td colSpan={COLUMN_COUNT} className="px-2 py-1 font-medium">
                      <span>{groupKey}</span>{' '}
                      <span className="text-text-secondary">({groupRows.length})</span>
                    </td>
                  </tr>
                  {groupRows.map((row) => (
                    <tr
                      key={row.rowKey}
                      data-testid={`${testIdPrefix}-row`}
                      className="border-b border-border-secondary"
                    >
                      <td className={bodyCell}>{row.stepLabel ?? '—'}</td>
                      <td className={bodyCell} data-status={row.stepStatus}>
                        {statusLabel(row.stepStatus)}
                      </td>
                      <td className={bodyCell}>{row.assignedNames || '—'}</td>
                      <td className={bodyCell} data-decision={row.decisionType ?? ''}>
                        {row.stepBackText ? (
                          // Cofnięcie nie jest drogą: nie ma krawędzi, więc zamiast opisu drogi
                          // stoi „Cofnięto: <z> → <do>"; komentarz (powód) jest w kolumnie obok.
                          <div data-testid={`${testIdPrefix}-step-back`}>{row.stepBackText}</div>
                        ) : row.routeText && HUMAN_DECISIONS.has(row.decisionType ?? '') ? (
                          // Decyzja człowieka z drogą: sama droga jest decyzją („Odrzuć: … → …").
                          // Techniczne „Zatwierdzono" (każda decyzja to `approve` + droga) wprowadzałoby
                          // w błąd przy drodze odrzucającej, więc go nie pokazujemy.
                          <div data-testid={`${testIdPrefix}-route`}>{row.routeText}</div>
                        ) : (
                          <>
                            {decisionLabel(row.decisionType)}
                            {row.routeText ? (
                              <div
                                data-testid={`${testIdPrefix}-route`}
                                className="text-xs text-text-secondary"
                              >
                                {row.routeText}
                              </div>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td className={bodyCell}>{row.deciderName || '—'}</td>
                      <td className={bodyCell}>{dateTimeOf(row.whenIso) || '—'}</td>
                      <td className={bodyCell}>{commentText(row)}</td>
                    </tr>
                  ))}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
